import React from 'react';

import { useTranslation } from 'react-i18next';

import { useBillingPaymentSettingsForChannel } from 'lane-shared/domains/billingPayments/hooks';
import {
  ChargePartial,
  Invoice,
  TaxPolicy,
} from 'lane-shared/domains/billingPayments/types';
import { useBpAdminFeeChargeCodeEnabled } from 'react-hooks';
import {
  getPricing,
  PricingParams,
} from 'domains-billing-calculation';
import { Currency, formatCurrencyWithSymbolOnly, formatCurrencyCodeAndSymbol } from 'domains-billing-currency';
import {
  graphQLToCurrency,
  currencyToPaymentCurrency,
} from 'domains-billing-web-mappers';

import { M, H4 } from 'components/typography';

import useUserLocale from 'hooks/useUserLocale';
import useIsAdminView from 'hooks/useIsAdminView';

import { InvoiceTotalWithBreakdown } from './InvoiceTotalWithBreakdown';
import styles from './styles.scss';

export function InvoiceTotal({
  invoice,
  charges,
}: {
  invoice: Invoice | undefined;
  charges: ChargePartial[];
}) {
  const { t } = useTranslation();
  const locale = useUserLocale();
  const [isAdminView] = useIsAdminView();
  const currency = invoice?.currency
    ? graphQLToCurrency(invoice.currency)
    : Currency.USD;
  const currencyCode = currencyToPaymentCurrency(currency);
  const { taxDetails } = useBillingPaymentSettingsForChannel({
    channelId: invoice?.groupId,
  });
  const isAdminFeeFeatureEnabled = useBpAdminFeeChargeCodeEnabled();
  const taxTotalLabel = t('abp.invoice.details.totalPriceIncludingTax', {
    taxName: taxDetails?.taxName || t('abp.invoice.details.tax').toLowerCase(),
  });

  const taxInclusive = taxDetails?.taxPolicy === TaxPolicy.INCLUSIVE;

  // Legacy path (flag off): preserve original 3-row layout and calculations.
  let legacySubtotal = 0;
  let legacyTax = 0;
  let legacyTotal = 0;

  // New path (flag on): separate breakdown for rate+markup, tax, admin fee, admin fee tax.
  let rateWithMarkupTotal = 0;
  let taxOnlyTotal = 0;
  let adminFeeTotal = 0;
  let adminFeeTaxTotal = 0;
  let newInvoiceTotal = 0;

  charges.forEach(charge => {
    // skip charge if voided
    if (charge.status === 'VOIDED') return;

    // no items — add charge amount to both paths
    if (charge.items.length === 0) {
      legacySubtotal += charge.amount;
      legacyTotal += charge.amount;
      rateWithMarkupTotal += charge.amount;
      newInvoiceTotal += charge.amount;

      return;
    }

    charge.items.forEach(item => {
      const params: PricingParams = {
        currency: currencyCode,
        locale,
        priceComponents: item.product,
        ...(isAdminFeeFeatureEnabled
          ? { inclusivePricing: taxInclusive, isAdminFeeFeatureEnabled: true }
          : {}),
      };

      const { grossTax, grossAdminFee, grossAdminFeeTax, listPrice, netPrice } = getPricing(params);

      // Legacy path
      const itemTax = grossTax + (isAdminFeeFeatureEnabled ? grossAdminFeeTax : 0);
      legacySubtotal += listPrice * item.quantity;
      legacyTax += itemTax * item.quantity;
      legacyTotal += (listPrice + itemTax) * item.quantity;

      // New path
      const rateWithMarkup = listPrice - (grossAdminFee ?? 0);
      rateWithMarkupTotal += rateWithMarkup * item.quantity;
      taxOnlyTotal        += (grossTax ?? 0) * item.quantity;
      adminFeeTotal       += (grossAdminFee ?? 0) * item.quantity;
      adminFeeTaxTotal    += (grossAdminFeeTax ?? 0) * item.quantity;
      newInvoiceTotal     += (netPrice ?? (listPrice + itemTax)) * item.quantity;
    });
  });

  if (!isAdminFeeFeatureEnabled && taxInclusive) {
    legacySubtotal += legacyTax;
  }

  const newPathSubtotal = taxInclusive ? rateWithMarkupTotal + taxOnlyTotal : rateWithMarkupTotal;
  const invoiceSubtotal = isAdminFeeFeatureEnabled ? newPathSubtotal : legacySubtotal;
  const invoiceTotal = isAdminFeeFeatureEnabled ? newInvoiceTotal : legacyTotal;
  const invoiceTax = isAdminFeeFeatureEnabled ? taxOnlyTotal : legacyTax;

  const taxLabel = taxDetails?.taxName || t('abp.invoice.details.tax');

  if (isAdminFeeFeatureEnabled) {
    return (
      <InvoiceTotalWithBreakdown
        currency={currency}
        locale={locale}
        taxInclusive={taxInclusive}
        taxLabel={taxLabel}
        taxTotalLabel={taxTotalLabel}
        invoiceSubtotal={invoiceSubtotal}
        invoiceTax={invoiceTax}
        adminFeeTotal={adminFeeTotal}
        adminFeeTaxTotal={adminFeeTaxTotal}
        invoiceTotal={invoiceTotal}
        isAdminView={isAdminView}
      />
    );
  }

  return (
    <div className={styles.invoiceTotalContainer}>
      <div className={styles.invoiceTotalGrid}>
        <M className={styles.item}>{t('abp.invoice.details.subtotal')}</M>
        <M className={styles.item}>{formatCurrencyWithSymbolOnly(currency, invoiceSubtotal, locale)}</M>
        <M className={styles.item}>
          {taxLabel}
          {taxInclusive ? ` ${t('abp.invoice.details.tax.included')}` : ''}
        </M>
        <M className={styles.item}>{formatCurrencyWithSymbolOnly(currency, invoiceTax, locale)}</M>
        <H4 className={styles.item}>{taxTotalLabel}</H4>
        <div className={styles.invoiceTotal}>
          <H4 className={styles.item}>
            {formatCurrencyCodeAndSymbol(currency, invoiceTotal, locale)}
          </H4>
        </div>
      </div>
    </div>
  );
}
